import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ErrorBox, PageHeader, Spinner, Stat } from "../../components/ui";
import { api } from "../../lib/api";
import { roleLabel } from "../../lib/format";
import { AuditTable } from "./audit";

export function OverviewPage() {
  const q = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.unwrap(api.client.GET("/v1/admin/overview")),
    refetchInterval: 60_000,
  });
  const recent = useQuery({
    queryKey: ["admin", "audit", "recent"],
    queryFn: () => api.unwrap(api.client.GET("/v1/admin/audit", { params: { query: { limit: 10 } } })),
  });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  const o = q.data;
  const total = Object.values(o.users_by_role).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      <PageHeader title="Visão geral" subtitle="Atualiza sozinha a cada minuto." />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Usuários" value={total} />
        <Stat label="Ativos (7 dias)" value={o.active_users_last_7d} />
        <Stat label="Sessões abertas" value={o.active_sessions} />
        <Stat label="Logins (24 h)" value={o.logins_last_24h} />
        <Stat label="Falhas de login (24 h)" value={o.failed_logins_last_24h} tone={o.failed_logins_last_24h > 20 ? "danger" : undefined} />
        <Stat label="Bloqueados" value={o.blocked_users} />
        <Stat
          label="Reuso de token (7 dias)"
          value={o.refresh_reuse_last_7d}
          tone={o.refresh_reuse_last_7d > 0 ? "danger" : "ok"}
        />
        <div className="card p-4">
          <p className="text-xs font-medium tracking-wide text-muted uppercase">Por papel</p>
          <ul className="mt-2 space-y-0.5 text-sm">
            {Object.entries(o.users_by_role).map(([role, n]) => (
              <li key={role} className="flex justify-between">
                <span className="text-muted">{roleLabel[role as keyof typeof roleLabel] ?? role}</span>
                <span className="font-semibold">{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {o.refresh_reuse_last_7d > 0 && (
        <p className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm">
          Um refresh token já usado foi reapresentado: sinal de sessão roubada. A sessão foi encerrada automaticamente;
          confira os eventos <code>auth.refresh_reuse_detected</code> na{" "}
          <Link to="/console/auditoria" search={{ action: "auth.refresh_reuse_detected" }} className="text-accent underline">
            auditoria
          </Link>
          .
        </p>
      )}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Atividade recente</h2>
          <Link to="/console/auditoria" className="text-sm text-accent hover:underline">
            Ver tudo
          </Link>
        </div>
        {recent.isPending ? <Spinner /> : recent.isError ? <ErrorBox error={recent.error} /> : <AuditTable items={recent.data.items} />}
      </section>
    </div>
  );
}
